<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import { useBookExposure } from '@/composables/tracks/useBookExposure'
import { resources } from '@/config/brands'

const props = withDefaults(defineProps<{
  id: string // 唯一id，用于曝光
  src: string // 真实图片地址
  alt?: string // 图片alt
  placeholder?: string // 占位图地址
  isReportImp?: boolean // 是否曝光埋点
  extra?: Record<string, any> // 埋点扩展数据
  heightRate?: number
  ignorePicturesAppear?: boolean // 某些图片过大, 以object-cover显示时 intersectionRatio 永远不会大于0.5 导致无法曝光埋点, 配置这个直接忽略大于 0.5 的判断
  smallTheater?: boolean // 是否小剧场内容，为 true 时封面右上角展示角标
}>(), {
  alt: '',
  placeholder: resources.bookCoverPlaceholder,
  isReportImp: true,
  heightRate: 133,
  ignorePicturesAppear: false,
})

const { bookExposure } = useBookExposure()
const imgSrc = ref('')
const hasExposed = ref(false)
const isIntersecting = ref(false)
const imgLoaded = ref(false)
const ob = ref<any>(null)

function onImgError() {
  imgSrc.value = props.placeholder
}

function onImgLoad() {
  imgLoaded.value = true
}

// 监听曝光与懒加载
onMounted(() => {
  ob.value = useIntersectionObserver(
    document.getElementById(props.id),
    ([{ isIntersecting: intersecting, intersectionRatio }]) => {
      isIntersecting.value = intersecting
      // 懒加载 - 可见度达到 10% 即开始加载
      if (intersecting && intersectionRatio >= 0.1 && !imgLoaded.value)
        imgSrc.value = props.src

      // 曝光埋点 - 可见度达到 50% 才触发曝光
      if (intersecting && props.isReportImp && !hasExposed.value && (props.ignorePicturesAppear || intersectionRatio >= 0.5)) {
        bookExposure({
          module_name: props.extra?.module_name || '',
          module_id: props.extra?.module_id || '',
          module_type: props.extra?.module_type || '',
          book_id: props.extra?.id || '',
          book_name: props.extra?.contentName || '',
          author: props.extra?.author || '',
        })
        hasExposed.value = true
      }
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: [0.1, 0.5], // 监听 10% 和 50% 两个阈值
    },
  )
})

onBeforeUnmount(() => {
  if (ob.value)
    ob.value.stop()
})
</script>

<template>
  <div class="overflow-hidden">
    <div
      :id="id"
      class="overflow-hidden w-full relative h-0 bg-n-bg-card"
      :style="{ paddingBottom: `${heightRate}%` }"
    >
      <!-- 真实图加载完成后隐藏占位图：避免 Safari 亚像素渲染时右侧露出白色占位图边（WGSQ-475） -->
      <img
        v-show="!imgLoaded"
        class="w-full left-0 absolute h-full object-cover top-0"
        :src="placeholder"
        :alt="alt"
      >

      <img
        v-if="imgSrc"
        class="w-full left-0 absolute h-full object-cover top-0 transition-opacity duration-300"
        :class="{ 'opacity-100': imgLoaded, 'opacity-0': !imgLoaded }"
        :src="imgSrc"
        :alt="alt"
        @load="onImgLoad"
        @error="onImgError"
      >

      <!-- 小剧场角标（small_theater） -->
      <img
        v-if="smallTheater"
        src="/images/vowpath/home/tag-audio.png"
        class="absolute top-0 right-0 w-20px h-18px"
        alt=""
      >
    </div>
  </div>
</template>
